'use client';

/**
 * DocumentScopeBadge Component
 *
 * Small chip/badge showing document scope (loan, personal, entity).
 * Uses GlassChip with color mapping per scope type.
 */

import type { ChipProps } from '@mui/material/Chip';
import { useI18n } from '../../hooks/useI18n';
import { GlassChip } from '../GlassChip/GlassChip';

// ============================================================================
// Types
// ============================================================================

export type DocumentScope = 'loan' | 'personal' | 'entity';

export interface DocumentScopeBadgeProps {
  /** The scope of the document */
  scope: DocumentScope;
  /** Size of the chip */
  size?: ChipProps['size'];
}

// ============================================================================
// Constants
// ============================================================================

const SCOPE_COLOR_MAP: Record<DocumentScope, ChipProps['color']> = {
  loan: 'info',
  personal: 'success',
  entity: 'secondary',
};

const SCOPE_I18N_MAP: Record<DocumentScope, string> = {
  loan: 'dataRoom.scope.loan',
  personal: 'dataRoom.scope.personal',
  entity: 'dataRoom.scope.entity',
};

// ============================================================================
// Component
// ============================================================================

export const DocumentScopeBadge = ({ scope, size = 'small' }: DocumentScopeBadgeProps): React.ReactElement => {
  const { t } = useI18n();

  return (
    <GlassChip
      label={t(SCOPE_I18N_MAP[scope])}
      color={SCOPE_COLOR_MAP[scope]}
      size={size}
      variant="filled"
      glowOnHover={false}
    />
  );
};

DocumentScopeBadge.displayName = 'DocumentScopeBadge';

export default DocumentScopeBadge;
